import { FullWidthBasicModal } from '@DesignSystem/modals';
import { isNull } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { getChartFormByType } from '@/reducers/dashboardChartsReducer';

import {
  GALLERY_TAB,
  PREVIEW_TEMPLATE_ID
} from '@experiment-management-shared/constants/visualizationConstants';
import { getURLQueryParams } from '@shared/utils/url';
import {
  getBuildInCharts,
  TEMPLATE_KEYS,
  TEMPLATE_SCOPE_TYPES,
  PANEL_CODE_LANGUAGES
} from '@experiment-management-shared/constants/chartsGallery';

import { BUILT_IN_CHART_TYPES } from '@/lib/appConstants';
import { getSavedGalleryTab } from '@/reducers/ui/visualizationsUiReducer';

import GalleryModal from './GalleryModal';
import PanelPreviewDialog from './PanelPreviewDialog';
import { TextButton, Tooltip } from '@ds';
import { DSDocumentationIcon, DSLibraryIcon } from '@design-system/icons';
import AddBuiltInVisualizationModal from '@experiment-management-shared/components/AddBuiltInVisualizationModal';
import usePanelTemplates from '@experiment-management-shared/api/library-panel/usePanelTemplates';
import usePanelTemplate from '@experiment-management-shared/api/library-panel/usePanelTemplate';
import useInitialChartStateByType from '@experiment-management-shared/components/AddBuiltInVisualizationModal/subcomponents/hooks/useInitialChartState';
import defaultPyTemplate from '@experiment-management-shared/constants/customPanelTemplates/defaultPyTemplate';

const { BUILT_IN, WORKSPACE } = TEMPLATE_SCOPE_TYPES;

const PANEL_NAME_MAP = {
  [BUILT_IN_CHART_TYPES.pcd]: '3D'
};

const PanelModal = props => {
  const location = useLocation();
  const queryParams = getURLQueryParams(location.hash);

  const chartType = queryParams.get('chartType');
  const templateId = queryParams.get('templateId');
  const chartId = queryParams.get('chartId');
  const instanceId = queryParams.get('instanceId');
  const revisionId = queryParams.get('revisionId');
  const isTemplateEditMode = queryParams.get('isEditMode') === 'true';
  const isPanelSelected = Boolean(chartType);
  const isEditMode = !isNull(chartId) || isTemplateEditMode;
  const buildInCharts = useSelector(state => getBuildInCharts(state));

  const chartForm = useSelector(state =>
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    getChartFormByType(state, { chartType })
  );
  const isCustomVisualization = chartType === BUILT_IN_CHART_TYPES.libraryPanel;

  const { data: template } = usePanelTemplate(
    { templateId, revisionId },
    { enabled: isCustomVisualization }
  );

  const {
    initialChartState,
    resetInitialChartState
  } = useInitialChartStateByType(chartType);

  const {
    data: workspaceTemplates,
    isLoading: isLoadingWorkspace
  } = usePanelTemplates({
    scope: WORKSPACE,
    filterLanguages: [PANEL_CODE_LANGUAGES.PYTHON]
  });

  const isLoading = {
    [WORKSPACE]: isLoadingWorkspace
  };

  const savedGalleryTab = useSelector(getSavedGalleryTab);
  const previewTemplateId = queryParams.get(PREVIEW_TEMPLATE_ID);
  const history = useHistory();
  const isJSCode = template?.code?.type === PANEL_CODE_LANGUAGES.JAVASCRIPT;

  const hasCodeBeenUpdated = useMemo(() => {
    if (
      chartType === BUILT_IN_CHART_TYPES.python ||
      chartType === BUILT_IN_CHART_TYPES.libraryPanel
    ) {
      return (
        chartForm?.code !== defaultPyTemplate &&
        chartForm?.code !== initialChartState?.code
      );
    }

    return false;
  }, [chartForm, chartType, initialChartState]);

  const handleCloseModal = () => {
    if (isJSCode) {
      history.push({ ...location, hash: '' }, { preventScroll: true });
    }
    if (hasCodeBeenUpdated) {
      const userConfirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to leave?'
      );
      if (!userConfirmed) {
        return;
      }
    }

    history.push({ ...location, hash: '' }, { preventScroll: true });
  };

  const templateByLabelMap = useMemo(() => {
    return {
      [BUILT_IN]: buildInCharts,
      [WORKSPACE]: workspaceTemplates
    };
  }, [workspaceTemplates]);

  const templatesByTabLabel = useMemo(
    () => templateByLabelMap[savedGalleryTab] || [],
    [savedGalleryTab, templateByLabelMap]
  );

  // Reset initial chart state when switching between panels
  useEffect(() => {
    if (!isPanelSelected) {
      resetInitialChartState();
    }
  }, [isPanelSelected]);

  const renderContent = () => {
    if (previewTemplateId) {
      if (isLoading[savedGalleryTab]) {
        return null;
      }

      const previewTemplate = templatesByTabLabel.find(
        t => t.id === previewTemplateId
      );

      if (previewTemplate) {
        return <PanelPreviewDialog open template={previewTemplate} />;
      }
    }

    if (isPanelSelected) {
      return (
        <AddBuiltInVisualizationModal
          instanceId={instanceId}
          initialChartState={initialChartState}
          resetInitialChartState={resetInitialChartState}
          {...props}
        />
      );
    }

    return <GalleryModal />;
  };

  const chartName =
    template?.templateName ?? (PANEL_NAME_MAP[chartType] || chartType);
  const isGridPanel = [
    BUILT_IN_CHART_TYPES.image,
    BUILT_IN_CHART_TYPES.video,
    BUILT_IN_CHART_TYPES.audio,
    BUILT_IN_CHART_TYPES.pcd
  ].includes(chartType);

  const action = isEditMode ? 'Edit' : 'Add';
  const type = isGridPanel || !chartType ? 'panel' : 'chart';
  const postfix = isEditMode ? '' : 'to project';
  const modalTitle = `${action} ${chartName || 'new'} ${type} ${postfix}`;
  const showPythonDocBtn = chartType === BUILT_IN_CHART_TYPES.python;

  const originPanelLibrary =
    queryParams.get(GALLERY_TAB) === TEMPLATE_KEYS.PANEL_LIBRARY;

  return (
    <FullWidthBasicModal
      variant="base"
      className="gallery-modal"
      maxWidth="xl"
      title={modalTitle}
      onClose={handleCloseModal}
      withoutFooter
      content={renderContent()}
      open
      disableEnforceFocus
      customHeaderContent={
        <>
          {chartType === BUILT_IN_CHART_TYPES.libraryPanel &&
            !originPanelLibrary && (
              <div className="panel-library-badge-container">
                <Tooltip
                  content={
                    <div className="panel-library-badge-tooltip">
                      Updating this panel will change it in the panel library,
                      and in the other views that use it.
                    </div>
                  }
                  placement="bottom"
                >
                  <div
                    className="panel-library-badge-wrapper"
                    data-test="panel-library-badge"
                  >
                    <DSLibraryIcon className="panel-library-badge-icon" />
                    Saved in panel library
                  </div>
                </Tooltip>
              </div>
            )}
          {showPythonDocBtn ? (
            <div className="python-panel-doc-button">
              <a
                href="/docs/v2/guides/comet-dashboard/code-panels/python-panels/"
                target="_blank"
              >
                <TextButton
                  type="secondary"
                  PrefixIcon={<DSDocumentationIcon />}
                >
                  Python panel docs
                </TextButton>
              </a>
            </div>
          ) : null}
        </>
      }
    />
  );
};

export default PanelModal;
